/**
 * Resources Calendar Theme
 *
 * Theme configuration for @howljs/calendar-kit.
 * Modern minimal design with Comfortaa and Noto Sans fonts.
 */

import { Colors } from '@/constants/theme';
import { getHeadingFont, getBodyFont } from '@/constants/typography';
import type { TextStyle, ViewStyle } from 'react-native';

export class ResourcesCalendarTheme {
  /**
   * Get theme configuration for resources calendar
   */
  static getTheme(colorScheme: 'light' | 'dark', accentColor: string) {
    const palette = Colors[colorScheme];

    return {
      colors: {
        primary: palette.primary, // Use mode accent (blue/orange) for current time indicator
        background: palette.background,
        surface: palette.backgroundLight,
        text: palette.text,
        border: palette.border,
      },
      hourHeight: 72,        // Height of each hour row (10 hours visible on screen)
      hourWidth: 53,         // Width of time column (matches @howljs/calendar-kit default)
      startHour: 0,          // Start at midnight
      endHour: 24,           // End at midnight (full day)
      columnWidth: 140,      // Width per resource column
    };
  }

  /**
   * Get styles for event cells - modern card style
   */
  static getEventStyles(colorScheme: 'light' | 'dark') {
    const palette = Colors[colorScheme];

    return {
      container: {
        borderRadius: 0,
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderWidth: 1,
      } as ViewStyle,
      title: {
        fontSize: 13,
        fontWeight: '600' as const,
        fontFamily: getBodyFont(),
        color: palette.text,
      } as TextStyle,
      time: {
        fontSize: 11,
        fontFamily: getBodyFont(),
        color: palette.textMuted,
      } as TextStyle,
      location: {
        fontSize: 11,
        fontFamily: getBodyFont(),
        color: palette.textMuted,
      } as TextStyle,
    };
  }

  /**
   * Get styles for time column
   */
  static getTimeColumnStyles(colorScheme: 'light' | 'dark') {
    const palette = Colors[colorScheme];

    return {
      width: 50,
      backgroundColor: palette.background,
      borderRightWidth: 1,
      borderRightColor: palette.border,
      textStyle: {
        fontSize: 11,
        fontFamily: getBodyFont(),
        color: palette.textMuted,
        fontWeight: '500' as const,
      } as TextStyle,
    };
  }

  /**
   * Get styles for resource header
   */
  static getResourceHeaderStyles(colorScheme: 'light' | 'dark') {
    const palette = Colors[colorScheme];

    return {
      height: 50,
      backgroundColor: palette.backgroundLight,
      borderBottomWidth: 1,
      borderBottomColor: palette.border,
      paddingHorizontal: 10,
      titleStyle: {
        fontSize: 14,
        fontWeight: '600' as const,
        fontFamily: getHeadingFont(),
        color: palette.text,
      } as TextStyle,
      subtitleStyle: {
        fontSize: 11,
        fontFamily: getBodyFont(),
        color: palette.textMuted,
      } as TextStyle,
    };
  }

  /**
   * Get styles for all-day events section
   */
  static getAllDaySectionStyles(colorScheme: 'light' | 'dark') {
    const palette = Colors[colorScheme];

    return {
      container: {
        backgroundColor: palette.background,
        minHeight: 36,
        maxHeight: 120,
      } as ViewStyle,
      eventChip: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 6,
        marginBottom: 4,
        marginHorizontal: 4,
      } as ViewStyle,
      eventText: {
        fontSize: 11,
        fontWeight: '600' as const,
        fontFamily: getBodyFont(),
        // Text color is computed dynamically per-event based on background contrast
      } as TextStyle,
      emptyText: {
        fontSize: 11,
        fontFamily: getBodyFont(),
        color: palette.textMuted,
        fontStyle: 'italic' as const,
        textAlign: 'center' as const,
      } as TextStyle,
    };
  }

  /**
   * Get styles for grid lines
   */
  static getGridStyles(colorScheme: 'light' | 'dark') {
    const palette = Colors[colorScheme];

    return {
      hourLine: {
        borderTopWidth: 1,
        borderTopColor: palette.border,
      } as ViewStyle,
      halfHourLine: {
        borderTopWidth: 1,
        borderTopColor: palette.borderMuted,
      } as ViewStyle,
    };
  }
}
